import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import type { FillBlankQuestion as FillBlankType } from '@/types/question-types';
import { renderMathInText } from '@/utils/renderMath';
import styles from './FillBlankQuestion.module.scss';

interface FillBlankQuestionProps {
  question: FillBlankType;
  userAnswer?: Record<number, string>;
  onAnswer: (answer: Record<number, string>) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
}

export function FillBlankQuestion({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
}: FillBlankQuestionProps) {
  const [answers, setAnswers] = useState<Record<number, string>>(
    userAnswer || {}
  );

  // Update answers when userAnswer prop changes
  useEffect(() => {
    if (userAnswer) {
      setAnswers(userAnswer);
    }
  }, [userAnswer]);

  const handleInputChange = (position: number, value: string) => {
    if (isReviewMode) return;

    const newAnswers = { ...answers, [position]: value };
    setAnswers(newAnswers);
    onAnswer(newAnswers);
  };

  const checkAnswer = (position: number): boolean => {
    if (!showExplanation) return false;

    const blank = question.content.blanks.find(b => b.position === position);
    if (!blank) return false;

    const userValue = answers[position]?.trim() || '';
    if (!userValue) return false;

    const caseSensitive = question.content.caseSensitive || false;

    return blank.acceptedAnswers.some(accepted =>
      caseSensitive
        ? accepted === userValue
        : accepted.toLowerCase() === userValue.toLowerCase()
    );
  };

  const getCorrectAnswer = (position: number): string => {
    const blank = question.content.blanks.find(b => b.position === position);
    return blank?.acceptedAnswers[0] || '';
  };

  const getInputStatus = (position: number): 'default' | 'correct' | 'incorrect' => {
    if (!showExplanation) return 'default';

    const userValue = answers[position]?.trim();
    if (!userValue) return 'default';

    return checkAnswer(position) ? 'correct' : 'incorrect';
  };

  // Parse question text and render with input fields
  const renderQuestionWithBlanks = () => {
    const parts = question.question_text.split(/\{\{blank\}\}/);
    const blanks = question.content.blanks;

    return (
      <div className={styles.fillBlank__questionContainer}>
        {parts.map((part, idx) => (
          <span key={idx}>
            {renderMathInText(part)}
            {idx < parts.length - 1 && (
              <motion.input
                type="text"
                className={`${styles.fillBlank__input} ${styles[`fillBlank__input--${getInputStatus(idx)}`]
                  }`}
                value={answers[idx] || ''}
                onChange={(e) => handleInputChange(idx, e.target.value)}
                placeholder={blanks[idx]?.placeholder || '...'}
                disabled={isReviewMode}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
              />
            )}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.fillBlank}>
      <div className={styles.fillBlank__question}>
        <h3 className={styles.fillBlank__questionText}>
          {renderQuestionWithBlanks()}
        </h3>
        <p className={styles.fillBlank__hint}>
          Fill in the blank{question.content.blanks.length > 1 ? 's' : ''} with the correct answer
          {question.content.blanks.length > 1 ? 's' : ''}
        </p>
      </div>

      {showExplanation && (
        <div className={styles.fillBlank__answers}>
          <h4 className={styles.fillBlank__answersTitle}>Correct Answers:</h4>
          {question.content.blanks.map((blank, idx) => {
            const isCorrect = checkAnswer(blank.position);
            const userValue = answers[blank.position]?.trim() || '(not answered)';
            const correctValue = getCorrectAnswer(blank.position);

            return (
              <div
                key={blank.id}
                className={`${styles.fillBlank__answerItem} ${isCorrect
                  ? styles['fillBlank__answerItem--correct']
                  : styles['fillBlank__answerItem--incorrect']
                  }`}
              >
                <div className={styles.fillBlank__answerLabel}>
                  Blank {idx + 1}:
                </div>
                <div className={styles.fillBlank__answerContent}>
                  <div className={styles.fillBlank__userAnswer}>
                    Your answer: <strong>{userValue}</strong>
                  </div>
                  {!isCorrect && (
                    <div className={styles.fillBlank__correctAnswer}>
                      Correct answer: <strong>{correctValue}</strong>
                    </div>
                  )}
                  {blank.acceptedAnswers.length > 1 && (
                    <div className={styles.fillBlank__alternates}>
                      Accepted variations: {blank.acceptedAnswers.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showExplanation && (
        <motion.div
          className={styles.fillBlank__explanation}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.fillBlank__explanationHeader}>
            <strong>Explanation:</strong>
          </div>
          <p className={styles.fillBlank__explanationText}>
            {renderMathInText(question.explanation)}
          </p>
        </motion.div>
      )}
    </div>
  );
}
